import React from "react";
import PropTypes from "prop-types";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { theme } from "./theme/theme.js";
import { AuthProvider } from "./components/contexts/AuthContext.jsx";
import { BrandingProvider } from "./components/contexts/BrandingContext.jsx";
import { ProtectedRoute } from "./components/ProtectedRoute.jsx";
import { PlanFeatureGate } from "./components/PlanFeatureGate.jsx";
import { PlanFeatureRequiredDialog } from "./components/PlanFeatureRequiredDialog.jsx";
import { AppLayout } from "./components/AppLayout.jsx";
import { Login } from "./Pages/Login/Login.jsx";
import { Register } from "./Pages/Register/Register.jsx";
import { VerifyEmail } from "./Pages/Register/VerifyEmail.jsx";
import { Welcome } from "./Pages/Register/Welcome.jsx";
import { RequestPasswordReset } from "./Pages/PasswordReset/RequestPasswordReset.jsx";
import { ResetPassword } from "./Pages/PasswordReset/ResetPassword.jsx";
import { Dashboard } from "./Pages/Dashboard/Dashboard.jsx";
import { Clients } from "./Pages/Clients/Clients.jsx";
import { Analytics } from "./Pages/Analytics/Analytics.jsx";
import { Projects } from "./Pages/Projects/Projects.jsx";
import { ProjectDetail } from "./Pages/Projects/ProjectDetail.jsx";
import { ProjectTemplates } from "./Pages/Projects/ProjectTemplates.jsx";
import { Conversations } from "./Pages/Conversations/Conversations.jsx";
import { Onboarding } from "./Pages/Onboarding/Onboarding.jsx";
import { Team } from "./Pages/Team/Team.jsx";
import { InternalCommunication } from "./Pages/InternalManagement/InternalCommunication.jsx";
import { Documentation } from "./Pages/InternalManagement/Documentation.jsx";
import { TechnicalSetup } from "./Pages/TechnicalSetup/TechnicalSetup.jsx";
import { Incidents } from "./Pages/Incidents/Incidents.jsx";
import { IncidentView } from "./Pages/Incidents/IncidentView.jsx";
import { Leads } from "./Pages/Leads/Leads.jsx";
import { Clientify } from "./Pages/Leads/Vista Clientify/Clientify.jsx";
import { ZonaSerseo } from "./Pages/ZonaSerseo/ZonaSerseo.jsx";
import { SettingsLayout, SettingsSection } from "./Pages/Settings/Settings.jsx";
import { settingsSectionForTab } from "./Pages/Settings/settingsSections.js";
import { settingsPath } from "./Pages/Settings/settingsPaths.js";
import { FEATURE_CLIENT_REPORTING, FEATURE_RECURRING_PROJECTS, FEATURE_SERSEO_CONTENT } from "./utils/agencyFeatures";
import { Videos } from "./Pages/ZonaSerseo/Videos.jsx";
import { Logs } from "./Pages/Logs/Logs.jsx";
import { ContentGenerator } from "./Pages/ContentGenerator/ContentGenerator.jsx";
import { ClientContent } from "./Pages/ContentGenerator/clientview/ClientContent.jsx";
import { ContentTutorialLayout } from "./Pages/ContentGenerator/ContentTutorialContext.jsx";
import { CampaignPage } from "./Pages/ContentGenerator/campaign/CampaignPage.jsx";
import { ContentPiecePage } from "./Pages/ContentGenerator/piece/ContentPiecePage.jsx";
import { NewPiecePage } from "./Pages/ContentGenerator/piece/NewPiecePage.jsx";
import { ManualPiecePage } from "./Pages/ContentGenerator/piece/ManualPiecePage.jsx";
import { ContentGroupPage } from "./Pages/ContentGenerator/group/ContentGroupPage.jsx";
import { Reports } from "./Pages/Reports/Reports.jsx";
import { ClientMetrics } from "./Pages/ClientMetrics/ClientMetrics.jsx";
import { DetailedReport } from "./Pages/Reports/DetailedReport.jsx";
import Leadmagnet from "./Pages/Leadmagnet/Leadmagnet.jsx";
import LeadmagnetProtected from "./Pages/Leadmagnet/LeadmagnetProtected.jsx";
import { PlanMarketing } from "./Pages/IA/PlanMarketing.jsx";
import LeadmagnetEjemplo from "./Pages/Leadmagnet/LeadmagnetEjemplo.jsx";
import { GoogleOAuthCallback } from "./Pages/Integrations/GoogleOAuthCallback.jsx";
import { PrivacyPolicy } from "./Pages/Legal/PrivacyPolicy.jsx";
import { useAuth } from "./components/contexts/AuthContext.jsx";
import { FreeClientSelectionGate } from "./components/FreeClientSelectionGate.jsx";
import { UserSeatsGate } from "./components/UserSeatsGate.jsx";

// URLs antiguas que ahora viven en Ajustes: /user?tab=X y /plan (vuelta de Stripe de sesiones en curso).
// Se conserva la query.
function LegacySettingsRedirect({ section }) {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const tab = params.get("tab");
  params.delete("tab");
  const query = params.toString() ? `?${params}` : "";
  return <Navigate to={`${settingsPath(tab ? settingsSectionForTab(tab) : section)}${query}`} replace />;
}

LegacySettingsRedirect.propTypes = { section: PropTypes.string.isRequired };

function LeadsIndexRedirect() {
  const { user } = useAuth();
  const requiresClientifyAccess = ["agency_admin", "consultant"].includes(user?.role);

  if (requiresClientifyAccess && !user?.clientify_id) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Navigate to="/leads/clientify" replace />;
}

function DefaultRedirect() {
  const { user } = useAuth();
  if (user?.role === "freelance_ticket") {
    return <Navigate to="/tickets" replace />;
  }
  return <Navigate to="/dashboard" replace />;
}

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <BrowserRouter>
          <AuthProvider>
            <BrandingProvider>
              <FreeClientSelectionGate />
              <UserSeatsGate />
              <PlanFeatureRequiredDialog />
              <Routes>
                {/* Public routes */}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/registro" element={<Register />} />
                <Route path="/verificar-email" element={<VerifyEmail />} />
                <Route path="/bienvenida" element={<Welcome />} />
                <Route path="/password-reset" element={<RequestPasswordReset />} />
                <Route path="/password-reset/:token" element={<ResetPassword />} />
                <Route path="/marketingplan" element={<Leadmagnet />} />
                <Route
                  path="/plan-marketing/ejemplo/:id"
                  element={<LeadmagnetEjemplo />}
                />

                {/* Linked from the Google OAuth consent screen: must stay public. */}
                <Route path="/privacidad" element={<PrivacyPolicy />} />

                {/* Where Google returns after the OAuth consent screen. Public:
                    it arrives with no token and only relays the outcome to the
                    window that opened it. */}
                <Route
                  path="/integrations/google/callback"
                  element={<GoogleOAuthCallback />}
                />

                {/* Protected routes */}
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <AppLayout />
                    </ProtectedRoute>
                  }
                >
                  {/* Default redirect */}
                  <Route index element={<DefaultRedirect />} />

                  {/* Dashboard - accessible to all, including freelance_ticket */}
                  <Route
                    path="dashboard"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "superadmin",
                          "agency_admin",
                          "consultant",
                          "client",
                          "freelance_ticket",
                        ]}
                      >
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                  {/* Ajustes: perfil, agencia y plataforma (ver Pages/Settings) */}
                  <Route
                    path="ajustes"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "superadmin",
                          "agency_admin",
                          "consultant",
                          "client",
                          "freelance_ticket",
                        ]}
                      >
                        <SettingsLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={null} />
                    <Route path=":section" element={<SettingsSection />} />
                  </Route>
                  <Route path="user" element={<LegacySettingsRedirect section="perfil" />} />
                  <Route path="plan" element={<LegacySettingsRedirect section="plan" />} />
                  {/* Clients - Restricted to super-admin, admin, consultant */}
                  <Route
                    path="clients"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "superadmin",
                          "agency_admin",
                          "consultant",
                        ]}
                      >
                        <Clients />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="analiticas"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "superadmin",
                          "agency_admin",
                          "consultant",
                        ]}
                      >
                        <Analytics />
                      </ProtectedRoute>
                    }
                  />

                  {/* Projects */}
                  <Route path="projects">
                    <Route
                      index
                      element={
                        <ProtectedRoute
                          allowedRoles={[
                            "superadmin",
                            "agency_admin",
                            "consultant",
                            "client",
                          ]}
                        >
                          <Navigate to="/projects/custom" replace />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="custom"
                      element={
                        <ProtectedRoute
                          allowedRoles={[
                            "superadmin",
                            "agency_admin",
                            "consultant",
                            "client",
                          ]}
                        >
                          <Projects projectType="custom" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="recurrent"
                      element={
                        <ProtectedRoute
                          allowedRoles={[
                            "superadmin",
                            "agency_admin",
                            "consultant",
                          ]}
                          requireAgencyFeature={FEATURE_RECURRING_PROJECTS}
                        >
                          <Projects projectType="recurrent" />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="templates"
                      element={
                        <ProtectedRoute
                          allowedRoles={[
                            "superadmin",
                            "agency_admin",
                            "consultant",
                          ]}
                        >
                          <ProjectTemplates />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path=":projectType/:projectId"
                      element={
                        <ProtectedRoute
                          allowedRoles={[
                            "superadmin",
                            "agency_admin",
                            "consultant",
                            "client",
                          ]}
                        >
                          <ProjectDetail />
                        </ProtectedRoute>
                      }
                    />
                  </Route>

                  {/* Conversations - freelance_ticket excluded */}
                  <Route
                    path="conversations"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "superadmin",
                          "agency_admin",
                          "consultant",
                          "client",
                        ]}
                      >
                        <Conversations />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="conversations/:clientId/:threadId"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "superadmin",
                          "agency_admin",
                          "consultant",
                          "client",
                        ]}
                      >
                        <Conversations />
                      </ProtectedRoute>
                    }
                  />

                  {/* Tickets - Restricted to super-admin, admin, consultant */}
                  <Route
                    path="tickets"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "superadmin",
                          "agency_admin",
                          "consultant",
                          "freelance_ticket",
                        ]}
                      >
                        <Incidents />
                      </ProtectedRoute>
                    }
                  />

                  {/* Individual Ticket View */}
                  <Route
                    path="tickets/:incidentId"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "superadmin",

                          "agency_admin",
                          "consultant",
                          "freelance_ticket",
                        ]}
                      >
                        <IncidentView />
                      </ProtectedRoute>
                    }
                  />

                  {/* Leads - Clientify for superadmin, agency_admin and consultant; Serseo only superadmin */}
                  <Route path="leads">
                    <Route
                      path="clientify"
                      element={
                        <ProtectedRoute
                          allowedRoles={[
                            "superadmin",
                            "agency_admin",
                            "consultant",
                          ]}
                          requireClientifyId
                        >
                          <Clientify />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="serseo"
                      element={
                        <ProtectedRoute allowedRoles={["superadmin"]}>
                          <Leads />
                        </ProtectedRoute>
                      }
                    />
                    {/* Redirect /leads to /leads/clientify by default */}
                    <Route index element={<LeadsIndexRedirect />} />
                  </Route>

                  {/* Team - Restricted to super-admin, admin */}
                  <Route
                    path="team"
                    element={
                      <ProtectedRoute
                        allowedRoles={["superadmin", "agency_admin"]}
                      >
                        <Team />
                      </ProtectedRoute>
                    }
                  />

                  {/* Gestión interna - equipo de la agencia */}
                  <Route
                    path="gestion-interna/comunicacion/:threadId?"
                    element={
                      <ProtectedRoute allowedRoles={["agency_admin", "consultant"]}>
                        <InternalCommunication />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="gestion-interna/documentacion"
                    element={
                      <ProtectedRoute allowedRoles={["agency_admin", "consultant"]}>
                        <Documentation />
                      </ProtectedRoute>
                    }
                  />

                  {/* Onboarding - Available to all authenticated users */}
                  <Route
                    path="onboarding"
                    element={
                      <ProtectedRoute allowedRoles={["client"]}>
                        <Onboarding />
                      </ProtectedRoute>
                    }
                  />

                  {/* Setup - freelance_ticket excluded */}
                  <Route
                    path="setup"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "superadmin",
                          "agency_admin",
                          "consultant",
                          "client",
                        ]}
                      >
                        <TechnicalSetup />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="setup/:projectId"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "superadmin",
                          "agency_admin",
                          "consultant",
                          "client",
                        ]}
                      >
                        <TechnicalSetup />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="contenido"
                    element={
                      <ProtectedRoute allowedRoles={["client"]} requireContentConfig>
                        <ClientContent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="contenido/:contentId"
                    element={
                      <ProtectedRoute allowedRoles={["client"]} requireContentConfig>
                        <ContentPiecePage isClientView />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="contenido/grupo/:groupId"
                    element={
                      <ProtectedRoute allowedRoles={["client"]} requireContentConfig>
                        <ContentGroupPage isClientView />
                      </ProtectedRoute>
                    }
                  />

                  {/* Zona SERSEO - Restricted to superadmin, agency_admin, consultant */}
                  <Route
                    path="Portal/ZonaSerseo"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "superadmin",
                          "agency_admin",
                          "consultant",
                        ]}
                        requireAgencyFeature={FEATURE_SERSEO_CONTENT}
                      >
                        <ZonaSerseo />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="Portal/Academia"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "superadmin",
                          "agency_admin",
                          "consultant",
                        ]}
                        requireAgencyFeature={FEATURE_SERSEO_CONTENT}
                      >
                        <Videos />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="Portal/Logs"
                    element={
                      <ProtectedRoute allowedRoles={["superadmin"]}>
                        <Logs />
                      </ProtectedRoute>
                    }
                  />



                  <Route
                    path="HerramientasIA/planes"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "superadmin",
                          "agency_admin",
                          "consultant",
                        ]}
                      >
                        <PlanMarketing />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="HerramientasIA/planes/:uuii"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "superadmin",
                          "agency_admin",
                          "consultant",
                        ]}
                      >
                        <LeadmagnetProtected />
                      </ProtectedRoute>
                    }
                  />
                  {/* Content Generator */}
                  <Route
                    path="HerramientasIA/contenidos"
                    element={
                      <ProtectedRoute
                        allowedRoles={[
                          "superadmin",
                          "agency_admin",
                          "consultant",
                        ]}
                      >
                        <ContentTutorialLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<ContentGenerator />} />
                    <Route path="generar" element={<CampaignPage />} />
                    <Route path="nuevo" element={<NewPiecePage />} />
                    <Route path="manual" element={<ManualPiecePage />} />
                    <Route path="pieza/:pieceId" element={<ContentPiecePage />} />
                    <Route path="grupo/:groupId" element={<ContentGroupPage />} />
                  </Route>

                  <Route path="HerramientasIA/informes">
                    <Route
                      index
                      element={
                        <ProtectedRoute
                          allowedRoles={[
                            "superadmin",
                            "agency_admin",
                            "consultant",
                          ]}
                        >
                          <PlanFeatureGate feature={FEATURE_CLIENT_REPORTING} title="Informes">
                            <Reports />
                          </PlanFeatureGate>
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path=":reportId"
                      element={
                        <ProtectedRoute
                          allowedRoles={[
                            "superadmin",
                            "agency_admin",
                            "consultant",
                          ]}
                        >
                          <PlanFeatureGate feature={FEATURE_CLIENT_REPORTING} title="Informes">
                            <DetailedReport />
                          </PlanFeatureGate>
                        </ProtectedRoute>
                      }
                    />
                  </Route>

                  {/* Métricas de integraciones (GA4, Search Console, SE Ranking) para clientes */}
                  <Route
                    path="metricas"
                    element={
                      <ProtectedRoute allowedRoles={["client"]} requireAgencyFeature={FEATURE_CLIENT_REPORTING}>
                        <ClientMetrics />
                      </ProtectedRoute>
                    }
                  />

                  {/* Ruta de informes para clientes */}
                  <Route path="informes">
                    <Route
                      index
                      element={
                        <ProtectedRoute allowedRoles={["client"]} requireAgencyFeature={FEATURE_CLIENT_REPORTING}>
                          <Reports />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path=":reportId"
                      element={
                        <ProtectedRoute allowedRoles={["client"]} requireAgencyFeature={FEATURE_CLIENT_REPORTING}>
                          <DetailedReport />
                        </ProtectedRoute>
                      }
                    />
                  </Route>

                </Route>

                {/* Fallback for unknown routes */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </BrandingProvider>
            <ToastContainer
              position="top-right"
              autoClose={5000}
              hideProgressBar={false}
              newestOnTop={false}
              closeOnClick
              rtl={false}
              pauseOnFocusLoss
              draggable
              pauseOnHover
              className="top-22!"
              toastClassName="!text-sm !rounded-md !shadow-lg border border-[var(--border)] bg-[var(--surface)] text-[var(--text)]"
            />
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
